import {
    RiAddFill,
    RiCalendarLine,
    RiDeleteBinLine,
    RiEdit2Line,
    RiEqualizerLine,
    RiFileCopyLine,
} from "@remixicon/react";
import React, { useState } from "react";
import TabList, { MenuItemOrDivider, TabItem } from "../../../components/theme/tab-list/TabList";

import {
    DashboardSettingsFiltersInput,
    DatePresets,
    GetReportsListDocument,
    MetricsDashboard,
    MetricsDashboardInput,
    PermissionAction,
    useDeleteMetricDashboardMutation,
    useSaveMetricDashboardMutation,
} from "../../../__generated__/api";
import { cloneWithoutTypename } from "../../../api";
import Filters from "../../../components/agent-v3/board-toolbar/Filters";
import useDeleteConfirm from "../../../components/confirm/Delete";
import DateRangeCalendarPicker, { DatePreferences } from "../../../components/pickers/date-range/DateRangeCalendarPicker";
import Button from "../../../components/theme/button/Button";
import { LoggingHandler } from "../../../contexts/logging/LoggingContext";
import useRouting, { Paths } from "../../../contexts/Routing";
import { parseFilterDates } from "../../../date";
import Styles from "./BoardToolbar.module.scss";
import { CreateEditReportView } from "./CreateEditReport";
import useRenameConfirm from "../../../components/confirm/Rename";
import createId from "../../../create-id";

export function createGraphId() {
    return `urn:fq:metrics-dashboard-graph:${createId()}`;
}

function createMenuItem(dashboard: Partial<MetricsDashboard>): TabItem<(typeof Paths)["REPORTS_GET"]> | undefined {
    return dashboard ? {
        id: dashboard.id,
        title: dashboard.name as string,
        canShowMenu: true,
        params: {
            reportId: dashboard.id,
        },
    } : undefined;
}
export default function BoardToolbar({
    filters,
    setFilters,
    onAddWidget,
    dashboards,
    selectedReport,
}: {
    filters: DashboardSettingsFiltersInput;
    setFilters: (value: DashboardSettingsFiltersInput) => void;
    onAddWidget: (() => void) | undefined;
    dashboards: MetricsDashboard[];
    selectedReport: MetricsDashboard | undefined;
}) {
    const [shouldEditView, setShouldEditView] = useState(false);
    const [editingItem, setEditingItem] = useState<MetricsDashboard>();

    const { getSearchParams, historyPushWithSearch } = useRouting();

    const [saveReport] = useSaveMetricDashboardMutation();
    const [deleteReport] = useDeleteMetricDashboardMutation();

    const { setDeleting: setDeletingReport, element: deleteReportElement } = useDeleteConfirm<MetricsDashboard>(deleting => {
        LoggingHandler.instance.wrap(async () => {
            await deleteReport({
                refetchQueries: [
                    { query: GetReportsListDocument },
                ],
                variables: {
                    id: deleting.id,
                },
            });
        });
    }, (view: MetricsDashboardInput) => view.name);

    const { setEditing, element: editReportElement } = useRenameConfirm<MetricsDashboard>(async (editItem, newName) => {
        LoggingHandler.instance.wrap(async () => {
            await saveReport({
                refetchQueries: [
                    { query: GetReportsListDocument },
                ],
                variables: {
                    id: editItem.id,
                    input: cloneWithoutTypename({
                        name: newName,
                        settings: editItem.settings,
                        permissions: editItem.permissions,
                        id: undefined,
                    } as MetricsDashboard),
                },
            });
        });
    }, item => item.name);

    const canDelete = true;

    return (
        <div data-tour-panel="board-top" className={`w-100 d-flex p3x-4 align-items-center ${Styles.panel}`}>
            <div className="flex-grow-1 border-0 align-items-bottom mt-auto">
                {deleteReportElement}
                {editReportElement}

                <CreateEditReportView
                    shouldShow={shouldEditView}
                    shouldEdit={editingItem != null}
                    value={editingItem}
                    onChange={value => {
                        if (value) {
                            LoggingHandler.instance.wrap(async () => {
                                const copy = cloneWithoutTypename(value as MetricsDashboard);
                                const createdReport = await saveReport({
                                    refetchQueries: [
                                        { query: GetReportsListDocument },
                                    ],
                                    variables: {
                                        input: {
                                            name: copy.name,
                                            permissions: copy.permissions,
                                            settings: copy.settings,
                                        },
                                        id: editingItem?.id,
                                    },
                                });
                                setEditingItem(undefined);
                                setShouldEditView(false);

                                historyPushWithSearch(
                                    Paths.REPORTS_GET,
                                    getSearchParams(Paths.REPORTS_GET),
                                    { reportId: createdReport.data?.saveMetricsDashboard?.id },
                                );
                            });
                        }
                        setShouldEditView(false);
                    }}
                    onHide={() => {
                        setShouldEditView(false);
                        setEditingItem(undefined);
                    }}
                />

                <TabList
                    className="border-0 "
                    onAdd={() => {
                        setEditingItem(undefined);
                        setShouldEditView(true);
                    }}
                    to={Paths.REPORTS_GET}
                    maxCount={5}
                    search={getSearchParams(Paths.REPORTS) as Record<string, string>}
                    selectedId={selectedReport?.id}
                    items={dashboards?.map(createMenuItem).filterNull() ?? []}
                    onSelected={async selectedId => {
                        const selected = dashboards.find(row => row.id === selectedId);
                        if (selected) {
                            historyPushWithSearch(
                                Paths.REPORTS_GET,
                                getSearchParams(Paths.REPORTS_GET),
                                { reportId: selectedId },
                            );
                        }
                    }}
                    menuItems={selectedReport?.currentUserPermissionAction === PermissionAction.Modify ? [
                        {
                            label: "Rename",
                            icon: <RiEdit2Line />,
                            onClick: item => {
                                const dashboard = dashboards.find(row => row.id === item.id);
                                setEditing(dashboard);
                            },
                        } as MenuItemOrDivider,
                        {
                            label: "Edit",
                            icon: <RiEqualizerLine />,
                            onClick: item => {
                                const dashboard = dashboards.find(row => row.id === item.id);
                                setEditingItem(dashboard);
                                setShouldEditView(true);
                            },
                        } as MenuItemOrDivider,

                        {
                            label: "Duplicate",
                            icon: <RiFileCopyLine />,
                            onClick: item => {
                                const dashboard = dashboards.find(row => row.id === item.id);
                                if (dashboard) {
                                    const copy: MetricsDashboardInput = {
                                        name: `${dashboard.name} (Copy)`,
                                        permissions: dashboard.permissions ?? [
                                            {
                                                action: PermissionAction.Execute,
                                            },
                                        ],
                                        settings: {
                                            ...dashboard.settings,
                                            graphs: dashboard.settings.graphs.map(row => ({
                                                ...row,
                                                id: createGraphId(),
                                            })),
                                        },
                                    };
                                    LoggingHandler.instance.wrap(async () => {
                                        const createdReport = await saveReport({
                                            refetchQueries: [
                                                { query: GetReportsListDocument },
                                            ],
                                            variables: {
                                                input: cloneWithoutTypename(copy as MetricsDashboard),
                                            },
                                        });

                                        historyPushWithSearch(
                                            Paths.REPORTS_GET,
                                            getSearchParams(Paths.REPORTS_GET),
                                            { reportId: createdReport.data?.saveMetricsDashboard?.id },
                                        );
                                    });
                                }
                            },
                        } as MenuItemOrDivider,
                        canDelete ? {
                            isDivider: true,
                        } as MenuItemOrDivider : undefined,

                        canDelete
                            ? {
                                label: "Delete",
                                icon: <RiDeleteBinLine />,
                                onClick: item => {
                                    const dashboard = dashboards.find(row => row.id === item.id);
                                    setDeletingReport(dashboard);
                                },
                            } as MenuItemOrDivider : undefined,
                    ].filterNull() : undefined}
                />
            </div>
            <div className={Styles["panel-filters"]}>
                <DateRangeCalendarPicker
                    icon={<RiCalendarLine />}
                    value={filters.createdDate as DatePreferences ?? { preset: DatePresets.Last_30Days }}
                    shouldPortal
                    onChange={createdDate => {
                        const date = parseFilterDates(createdDate);
                        setFilters({
                            ...filters,
                            createdDate: {
                                ...createdDate,
                                startDate: date.startDate ? new Date(date.startDate) : undefined,
                                endDate: date.endDate ? new Date(date.endDate) : undefined,
                            },
                        });
                    }}
                />
                <Filters
                    onChange={value => {
                        setFilters(value);
                    }}
                    value={filters}
                    types={{
                        assignees: true,
                        conversationSources: true,
                        forms: true,
                        statuses: true,
                        priorities: true,
                        queues: true,
                        requesters: true,
                        requestTypes: true,
                    }}
                />
                {onAddWidget && (
                    <Button prefixIcon={<RiAddFill />} variant="primary" size="small" onClick={onAddWidget}>
                        Add Widget
                    </Button>
                )}
            </div>
        </div>
    );
}
